import { useState } from "react";
import { getClientPaymentMatrix } from "../../lib/calculations";
import { formatUSD } from "../../lib/formatters";
import Modal from "../ui/Modal";

const STATUS_CELL = {
  paid: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300",
  pending: "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300",
  scheduled: "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300",
  mixed: "bg-violet-50 text-violet-700 hover:bg-violet-100 dark:bg-violet-900/20 dark:text-violet-300",
};

export default function PaymentMatrix({ clientId, payments, offers, monthsAhead }) {
  const matrix = getClientPaymentMatrix(clientId, payments, offers, monthsAhead);
  const [cellModal, setCellModal] = useState(null);

  if (matrix.months.length === 0) {
    return (
      <div className="text-sm text-brand-slate py-4">No payments recorded yet for this client.</div>
    );
  }

  return (
    <>
      <div className="matrix-scroll overflow-x-auto rounded-lg border border-gray-100 dark:border-[var(--border-soft)]">
        <table className="w-max text-xs">
          <thead className="bg-gray-50 dark:bg-[var(--bg-soft)] sticky top-0">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 dark:bg-[var(--bg-soft)] text-left px-3 py-2 font-medium text-brand-slate uppercase tracking-wide border-r border-gray-100 dark:border-[var(--border-soft)] min-w-[160px]">
                Metric
              </th>
              {matrix.months.map((m, i) => (
                <th key={i} className="px-2 py-2 text-center font-medium text-brand-slate uppercase tracking-wide whitespace-nowrap min-w-[80px]">
                  {m.toLocaleDateString("en-US", { month: "short", year: "2-digit" })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Revenue row */}
            <tr className="border-t border-gray-100 dark:border-[var(--border-soft)]">
              <td className="sticky left-0 z-10 bg-white dark:bg-[var(--bg-surface)] px-3 py-2 border-r border-gray-100 dark:border-[var(--border-soft)] font-medium">
                Revenue
              </td>
              {matrix.revenueCells.map((cell, ci) => (
                <td
                  key={ci}
                  className={`p-1 text-center min-w-[80px] ${cell.status ? "cursor-pointer" : ""}`}
                  onClick={() => cell.status && setCellModal({ label: "Revenue", cell })}
                >
                  {cell.status ? (
                    <div className={`rounded px-1.5 py-1 ${STATUS_CELL[cell.status] || STATUS_CELL.mixed}`}>
                      {formatUSD(cell.amount)}
                    </div>
                  ) : (
                    <div className="text-gray-300 dark:text-gray-700">·</div>
                  )}
                </td>
              ))}
            </tr>
            {/* Cash collected row */}
            <tr className="border-t border-gray-100 dark:border-[var(--border-soft)] bg-gray-50/50 dark:bg-[var(--bg-soft)]/30">
              <td className="sticky left-0 z-10 bg-gray-50/50 dark:bg-[var(--bg-soft)]/30 px-3 py-2 border-r border-gray-100 dark:border-[var(--border-soft)] font-medium">
                Cash collected
              </td>
              {matrix.collectedCells.map((cell, ci) => {
                const revenueAmt = matrix.revenueCells[ci]?.amount || 0;
                const gap = revenueAmt - cell.amount;
                const hasGap = revenueAmt > 0 && gap > 0;
                return (
                  <td
                    key={ci}
                    className={`p-1 text-center min-w-[80px] ${cell.amount > 0 ? "cursor-pointer" : ""}`}
                    onClick={() => cell.amount > 0 && setCellModal({ label: "Cash collected", cell })}
                  >
                    {cell.amount > 0 ? (
                      <div className="rounded px-1.5 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                        {formatUSD(cell.amount)}
                      </div>
                    ) : hasGap ? (
                      <div className="rounded px-1.5 py-1 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 text-[10px]">
                        −{formatUSD(gap)}
                      </div>
                    ) : (
                      <div className="text-gray-300 dark:text-gray-700">·</div>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <Modal open={!!cellModal} onClose={() => setCellModal(null)} title={cellModal?.label || "Payments"}>
        {cellModal && (
          <div className="space-y-2 text-sm">
            {cellModal.cell.payments.map((p) => {
              const offer = offers.find((o) => o.id === p.offerId);
              return (
                <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-[var(--bg-soft)]">
                  <div>
                    <div className="font-medium">{formatUSD(p.amount)}</div>
                    <div className="text-xs text-brand-slate">
                      {offer?.name || "No offer"} · {p.note || p.status}
                    </div>
                  </div>
                  <div className="text-xs uppercase tracking-wide text-brand-slate">{p.status}</div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
}
